/**
 * Compact storefront-style preview: fixed viewport, overlayed popup.
 * Market ask = Country (+ optional Language) chooser + Continue (§32–33).
 * Store redirect path can still pass a stay link (no chooser).
 */

function flagEmoji(cc: string): string {
  const code = String(cc || "").toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  try {
    return String.fromCodePoint(
      0x1f1e6 + code.charCodeAt(0) - 65,
      0x1f1e6 + code.charCodeAt(1) - 65,
    );
  } catch {
    return "";
  }
}

export function PopupPreview({
  style,
  title,
  message,
  helper,
  button,
  stay,
  buttonColor,
  device,
  buttonRadius = 8,
  titleSize = 20,
  textSize = 14,
  showBlur = true,
  buttonAlign = "stretch",
  chooser = true,
  countries = [],
  languages = [],
  selectedCountry,
  selectedLanguage,
  showCurrency = true,
  showLanguage = false,
  showFlags = true,
  currencies = {},
}: {
  style: string;
  title: string;
  message: string;
  helper?: string;
  button: string;
  stay?: string;
  buttonColor: string;
  device: "desktop" | "mobile";
  buttonRadius?: number;
  titleSize?: number;
  textSize?: number;
  showBlur?: boolean;
  buttonAlign?: "left" | "stretch" | "right";
  /** Market ask: Country (+ optional Language). */
  chooser?: boolean;
  countries?: Array<{ code: string; label: string }>;
  languages?: Array<{ id: string; label: string }>;
  selectedCountry?: string;
  selectedLanguage?: string;
  showCurrency?: boolean;
  showLanguage?: boolean;
  showFlags?: boolean;
  currencies?: Record<string, string>;
}) {
  const color = buttonColor || "#000000";
  const isBar = style === "bar";
  const isSheet = style === "sheet";
  const isCenter = style === "center" || style === "popup";
  const isCard = isCenter || isSheet || (!isBar && style === "corner");
  const isMobile = device === "mobile";
  const layoutClass = isBar
    ? "sg-preview-popup sg-preview-popup--bar"
    : isSheet
      ? "sg-preview-popup sg-preview-popup--sheet"
      : isCenter
        ? "sg-preview-popup sg-preview-popup--center"
        : "sg-preview-popup sg-preview-popup--corner";
  const actionsAlign =
    buttonAlign === "left"
      ? "flex-start"
      : buttonAlign === "right"
        ? "flex-end"
        : "stretch";
  const countryOpts =
    countries.length > 0
      ? countries.map((c) => {
          const cur = showCurrency
            ? String(currencies[c.code] || "").toUpperCase()
            : "";
          const flag = showFlags ? flagEmoji(c.code) : "";
          let label = c.label;
          // Avoid double-appending if caller already baked currency in.
          if (cur && !label.includes(` · ${cur}`)) {
            label = `${label} · ${cur}`;
          }
          if (flag && !label.startsWith(flag)) {
            label = `${flag} ${label}`;
          }
          return { code: c.code, label };
        })
      : [{ code: "US", label: showFlags ? "🇺🇸 United States" : "United States" }];
  const languageOpts =
    showLanguage && languages.length > 1 ? languages : [];
  const countryValue =
    selectedCountry &&
    countryOpts.some((c) => c.code === selectedCountry)
      ? selectedCountry
      : countryOpts[0]?.code;
  const languageValue =
    selectedLanguage &&
    languageOpts.some((l) => l.id === selectedLanguage)
      ? selectedLanguage
      : languageOpts[0]?.id;

  return (
    <div
      className={`sg-preview-stage${isMobile ? " sg-preview-stage--mobile" : " sg-preview-stage--desktop"}`}
    >
      <div className="sg-preview-viewport" aria-hidden={false}>
        <div className="sg-preview-bg">
          <div className="sg-preview-bg-chrome" />
          <div className="sg-preview-bg-lines">
            <span />
            <span />
            <span />
          </div>
        </div>

        {isCenter || isSheet ? (
          <div
            className="sg-preview-dim"
            style={showBlur ? undefined : { backdropFilter: "none" }}
          />
        ) : null}

        <div
          className={layoutClass}
          role="dialog"
          aria-label="Popup preview"
        >
          {isCard ? (
            <span className="sg-preview-x" aria-hidden>
              ✕
            </span>
          ) : null}
          <div className="sg-preview-popup-body">
            <p
              className="sg-preview-title"
              style={{ fontSize: titleSize }}
            >
              {title}
            </p>
            <p className="sg-preview-msg" style={{ fontSize: textSize }}>
              {message}
            </p>
            {helper ? (
              <p className="sg-preview-helper" style={{ fontSize: textSize }}>
                {helper}
              </p>
            ) : null}
            {chooser ? (
              <div className="sg-preview-chooser">
                <label className="sg-preview-field">
                  <span>Country</span>
                  <select
                    aria-label="Country"
                    value={countryValue}
                    tabIndex={-1}
                    onChange={() => undefined}
                  >
                    {countryOpts.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
                {languageOpts.length > 0 ? (
                  <label className="sg-preview-field">
                    <span>Language</span>
                    <select
                      aria-label="Language"
                      value={languageValue}
                      tabIndex={-1}
                      onChange={() => undefined}
                    >
                      {languageOpts.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
            ) : null}
            <div
              className="sg-preview-actions"
              style={{ alignItems: actionsAlign }}
            >
              <button
                type="button"
                className="sg-preview-go"
                style={{
                  background: color,
                  borderRadius: buttonRadius,
                  width: buttonAlign === "stretch" ? "100%" : undefined,
                }}
                tabIndex={-1}
              >
                {button}
              </button>
              {stay ? (
                <button type="button" className="sg-preview-stay" tabIndex={-1}>
                  {stay}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
